import type {
  CreateSubanalysisStepInput,
  SubanalysisDefinitionInput,
} from "@/domains/templates/subanalysis.types";
import { supabase } from "@/shared/lib/supabase";
import { getErrorMessage } from "@/shared/lib/error-message";
import type {
  OutputTypeEnum,
  StepWithSettings,
  TemplateVariableInput,
  TemplateWithSettings,
  Variable,
} from "@/domains/templates/types";

type FunctionErrorWithContext = {
  context?: unknown;
};

function errorFromPayload(payload: unknown, fallback: string): Error {
  const normalized = new Error(getErrorMessage(payload, fallback));
  if (payload && typeof payload === "object") {
    Object.assign(normalized, payload);
  }
  return normalized;
}

async function throwFunctionInvokeError(error: unknown): Promise<never> {
  const context =
    error && typeof error === "object" && "context" in error
      ? (error as FunctionErrorWithContext).context
      : undefined;

  if (context instanceof Response) {
    const payload: unknown = await context
      .clone()
      .json()
      .catch(() => error);
    throw Object.assign(
      errorFromPayload(payload, "Template API request failed"),
      { status: context.status },
    );
  }

  const fallbackError = context ?? error;
  if (fallbackError instanceof Error) throw fallbackError;
  throw errorFromPayload(fallbackError, "Template API request failed");
}

function normalizeCanonicalStep(payload: unknown): StepWithSettings {
  const { analysisstep_setting, ...step } = payload as Omit<
    StepWithSettings,
    "templatestep_setting"
  > & {
    analysisstep_setting: StepWithSettings["templatestep_setting"];
  };
  return { ...step, templatestep_setting: analysisstep_setting };
}

function normalizeCanonicalTemplate(payload: unknown): TemplateWithSettings {
  const { analysis_setting, ...template } = payload as Omit<
    TemplateWithSettings,
    "template_setting"
  > & {
    analysis_setting: TemplateWithSettings["template_setting"];
  };
  return { ...template, template_setting: analysis_setting };
}

function templateVariablePath(params: {
  templateId: string;
  stepId?: string;
  variableId?: string;
}): string {
  const scope = params.stepId
    ? `template/${params.templateId}/step/${params.stepId}/variables`
    : `template/${params.templateId}/variables`;
  return params.variableId ? `${scope}/${params.variableId}` : scope;
}

export const templateBuilderMutations = {
  createSubanalysisStep: async (params: {
    templateId: string;
    definition: CreateSubanalysisStepInput;
  }): Promise<StepWithSettings> => {
    const response = await supabase.functions.invoke(
      `template/${params.templateId}/subanalysis-step`,
      {
        method: "POST",
        body: params.definition,
      },
    );
    if (response.error) await throwFunctionInvokeError(response.error);
    return normalizeCanonicalStep(response.data);
  },
  updateSubanalysisStep: async (params: {
    templateId: string;
    stepId: string;
    definition: SubanalysisDefinitionInput & {
      expected_record_updated: string;
    };
  }): Promise<StepWithSettings> => {
    const response = await supabase.functions.invoke(
      `template/${params.templateId}/step/${params.stepId}/subanalysis-config`,
      {
        method: "PATCH",
        body: params.definition,
      },
    );
    if (response.error) await throwFunctionInvokeError(response.error);
    return normalizeCanonicalStep(response.data);
  },
  createTemplate: async (params: {
    name: string;
    description?: string;
  }): Promise<string> => {
    const response = await supabase.functions.invoke("template", {
      method: "POST",
      body: {
        name: params.name.trim(),
        description: params.description?.trim() ?? null,
      },
    });
    if (response.error) await throwFunctionInvokeError(response.error);

    const payload: unknown = response.data;
    if (
      !payload ||
      typeof payload !== "object" ||
      !("analysis_id" in payload) ||
      typeof (payload as { analysis_id?: unknown }).analysis_id !== "string"
    ) {
      throw new Error("Missing analysis id from template creation");
    }
    return (payload as { analysis_id: string }).analysis_id;
  },

  deleteTemplate: async (templateId: string): Promise<void> => {
    const response = await supabase.functions.invoke(`template/${templateId}`, {
      method: "DELETE",
    });
    if (response.error) await throwFunctionInvokeError(response.error);
  },

  createVariable: async (params: {
    templateId: string;
    stepId?: string;
    variable: TemplateVariableInput;
  }): Promise<Variable> => {
    const response = await supabase.functions.invoke(
      templateVariablePath(params),
      { method: "POST", body: params.variable },
    );
    if (response.error) await throwFunctionInvokeError(response.error);
    return response.data as Variable;
  },

  createVariableHierarchy: async (params: {
    templateId: string;
    stepId?: string;
    variable: TemplateVariableInput;
  }): Promise<Variable[]> => {
    const path = params.stepId
      ? `template/${params.templateId}/step/${params.stepId}/variables/hierarchy`
      : `template/${params.templateId}/variables/hierarchy`;
    const response = await supabase.functions.invoke(path, {
      method: "POST",
      body: { variable: params.variable },
    });
    if (response.error) await throwFunctionInvokeError(response.error);
    return response.data as Variable[];
  },

  updateVariable: async (params: {
    templateId: string;
    stepId?: string;
    variableId: string;
    variable: TemplateVariableInput;
  }): Promise<Variable> => {
    const response = await supabase.functions.invoke(
      templateVariablePath(params),
      { method: "PATCH", body: params.variable },
    );
    if (response.error) await throwFunctionInvokeError(response.error);
    return response.data as Variable;
  },

  deleteVariable: async (params: {
    templateId: string;
    stepId?: string;
    variableId: string;
  }): Promise<{ deleted_variable_ids: string[] }> => {
    const response = await supabase.functions.invoke(
      templateVariablePath(params),
      { method: "DELETE" },
    );
    if (response.error) await throwFunctionInvokeError(response.error);
    return response.data as { deleted_variable_ids: string[] };
  },

  updateTemplateMetadata: async (params: {
    templateId: string;
    name?: string;
    description?: string | null;
    user_context?: string | null;
  }): Promise<TemplateWithSettings> => {
    const { templateId, ...body } = params;
    const response = await supabase.functions.invoke(`template/${templateId}`, {
      method: "PATCH",
      body,
    });
    if (response.error) await throwFunctionInvokeError(response.error);
    return normalizeCanonicalTemplate(response.data);
  },

  updateTemplateStepMetadata: async (params: {
    templateId: string;
    stepId: string;
    display_name?: string;
    display_description?: string | null;
    display_output_types?: OutputTypeEnum[];
  }): Promise<StepWithSettings> => {
    const { templateId, stepId, ...body } = params;
    const response = await supabase.functions.invoke(
      `template/${templateId}/step/${stepId}`,
      { method: "PATCH", body },
    );
    if (response.error) await throwFunctionInvokeError(response.error);
    return normalizeCanonicalStep(response.data);
  },

  createTemplateStep: async (params: {
    templateId: string;
    position: number;
    templateStepId?: string;
  }): Promise<StepWithSettings> => {
    const response = await supabase.functions.invoke(
      `template/${params.templateId}/createStep`,
      {
        method: "POST",
        body: {
          position: params.position,
          template_step_id: params.templateStepId,
        },
      },
    );
    if (response.error) await throwFunctionInvokeError(response.error);
    return normalizeCanonicalStep(response.data);
  },

  createTemplateStepTop: async (params: {
    templateId: string;
    templateStepId?: string;
  }): Promise<StepWithSettings> => {
    const response = await supabase.functions.invoke(
      `template/${params.templateId}/insertTop`,
      {
        method: "POST",
        body: { template_step_id: params.templateStepId },
      },
    );
    if (response.error) await throwFunctionInvokeError(response.error);
    return normalizeCanonicalStep(response.data);
  },

  createTemplateStepBottom: async (params: {
    templateId: string;
    templateStepId?: string;
  }): Promise<StepWithSettings> => {
    const response = await supabase.functions.invoke(
      `template/${params.templateId}/insertBottom`,
      {
        method: "POST",
        body: { template_step_id: params.templateStepId },
      },
    );
    if (response.error) await throwFunctionInvokeError(response.error);
    return normalizeCanonicalStep(response.data);
  },

  deleteTemplateStep: async (params: {
    templateId: string;
    stepId: string;
  }): Promise<{ deleted_analysis_step_id: string }> => {
    const response = await supabase.functions.invoke(
      `template/${params.templateId}/step/${params.stepId}`,
      { method: "DELETE" },
    );
    if (response.error) await throwFunctionInvokeError(response.error);
    return response.data as { deleted_analysis_step_id: string };
  },

  moveTemplateStep: async (params: {
    templateId: string;
    stepId: string;
    analysisOrder: number;
  }): Promise<{
    steps: Array<{ analysis_step_id: string; analysis_order: number }>;
  }> => {
    const response = await supabase.functions.invoke(
      `template/${params.templateId}/step/${params.stepId}/order`,
      {
        method: "PATCH",
        body: { analysis_order: params.analysisOrder },
      },
    );
    if (response.error) await throwFunctionInvokeError(response.error);
    return response.data as {
      steps: Array<{ analysis_step_id: string; analysis_order: number }>;
    };
  },
};
